"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileNameWithAiMarker from "@/components/common/profile/ProfileNameWithAiMarker";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import WaveDropTime from "./time/WaveDropTime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { DropAuthorBadges } from "./DropAuthorBadges";
import { useCallback } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useCompactMode } from "@/contexts/CompactModeContext";
import type { DropTimestampLayout } from "./drop.types";
interface WaveDropHeaderProps {
  readonly drop: ApiDrop;
  readonly isStorm: boolean;
  readonly currentPartIndex: number;
  readonly partsCount: number;
  readonly showWaveInfo: boolean;
  readonly badge?: React.ReactNode | undefined;
  readonly showActionsButton?: boolean | undefined;
  readonly onOpenActions?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
}
const WaveDropHeader: React.FC<WaveDropHeaderProps> = t0 => {
  const $ = _c(66);
  const {
    drop,
    isStorm,
    currentPartIndex,
    partsCount,
    showWaveInfo,
    badge,
    showActionsButton: t1,
    onOpenActions,
    timestampLayout: t2
  } = t0;
  const showActionsButton = t1 === undefined ? false : t1;
  const timestampLayout = t2 === undefined ? "inline" : t2;
  const router = useRouter();
  const compact = useCompactMode();
  const authorIdentity = drop.author.handle ?? drop.author.primary_address;
  const isStackedTimestamp = timestampLayout === "stacked";
  let t3;
  if ($[0] !== router) {
    t3 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[0] = router;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleNavigation = t3;
  const t4 = isStackedTimestamp ? "tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-y-1.5" : "tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1";
  const t5 = `tw-mb-0 tw-font-semibold tw-leading-none ${compact ? "tw-text-sm" : "tw-text-md"}`;
  let t6;
  if ($[2] !== authorIdentity || $[3] !== handleNavigation) {
    t6 = e_0 => handleNavigation(e_0, `/${authorIdentity}`);
    $[2] = authorIdentity;
    $[3] = handleNavigation;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = `/${authorIdentity}`;
  let t8;
  if ($[5] !== authorIdentity || $[6] !== drop.author.classification) {
    t8 = <ProfileNameWithAiMarker classification={drop.author.classification}>{authorIdentity}</ProfileNameWithAiMarker>;
    $[5] = authorIdentity;
    $[6] = drop.author.classification;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t6 || $[9] !== t7 || $[10] !== t8) {
    t9 = <Link onClick={t6} href={t7} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-opacity-80 desktop-hover:hover:tw-underline">{t8}</Link>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== authorIdentity || $[13] !== t9) {
    t10 = <UserProfileTooltipWrapper user={authorIdentity}>{t9}</UserProfileTooltipWrapper>;
    $[12] = authorIdentity;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t5) {
    t11 = <p className={t5}>{t10}</p>;
    $[15] = t10;
    $[16] = t5;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== drop.author.level) {
    t12 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[18] = drop.author.level;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = `header-author-badges-${drop.id}`;
  let t14;
  if ($[20] !== drop.author || $[21] !== t13) {
    t14 = <DropAuthorBadges profile={drop.author} tooltipIdPrefix={t13} showProfileWaveDetails={true} />;
    $[20] = drop.author;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== isStackedTimestamp) {
    t15 = !isStackedTimestamp && <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[23] = isStackedTimestamp;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== drop.created_at || $[26] !== isStackedTimestamp) {
    t16 = !isStackedTimestamp && <WaveDropTime timestamp={drop.created_at} />;
    $[25] = drop.created_at;
    $[26] = isStackedTimestamp;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t11 || $[29] !== t12 || $[30] !== t14 || $[31] !== t15 || $[32] !== t16) {
    t17 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1">{t11}{t12}{t14}{t15}{t16}</div>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== drop.created_at || $[35] !== isStackedTimestamp) {
    t18 = isStackedTimestamp && <WaveDropTime timestamp={drop.created_at} />;
    $[34] = drop.created_at;
    $[35] = isStackedTimestamp;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t17 || $[38] !== t18 || $[39] !== t4) {
    t19 = <div className={t4}>{t17}{t18}</div>;
    $[37] = t17;
    $[38] = t18;
    $[39] = t4;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== badge) {
    t20 = Boolean(badge) && <div className="tw-ml-2">{badge}</div>;
    $[41] = badge;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t19 || $[44] !== t20) {
    t21 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1">{t19}{t20}</div>;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== onOpenActions || $[47] !== showActionsButton) {
    t22 = showActionsButton && onOpenActions !== undefined && <button type="button" aria-label="Open drop actions" onClick={onOpenActions} className="tw-hidden tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-1.5 tw-text-iron-300 active:tw-bg-iron-800 desktop-hover:hover:tw-bg-iron-900 desktop-hover:hover:tw-text-iron-100 md:tw-flex"><EllipsisVerticalIcon className="tw-h-5 tw-w-5" /></button>;
    $[46] = onOpenActions;
    $[47] = showActionsButton;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t21 || $[50] !== t22) {
    t23 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-2">{t21}{t22}</div>;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== drop.wave || $[53] !== handleNavigation || $[54] !== showWaveInfo) {
    t24 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage: waveMeta?.scope?.group?.is_direct_message ?? false,
        isApp: false
      });
      return <Link onClick={e_1 => handleNavigation(e_1, waveHref)} href={waveHref} className="tw-leading-0 -tw-mt-1 tw-mb-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[52] = drop.wave;
    $[53] = handleNavigation;
    $[54] = showWaveInfo;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t24) {
    t25 = <div>{t24}</div>;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== currentPartIndex || $[59] !== isStorm || $[60] !== partsCount) {
    t26 = isStorm && <div className="tw-relative tw-mt-2 tw-inline-flex"><span className="tw-mb-1.5 tw-text-xs tw-text-iron-50">{currentPartIndex + 1} /{" "}<span className="tw-text-iron-400">{partsCount}</span></span></div>;
    $[58] = currentPartIndex;
    $[59] = isStorm;
    $[60] = partsCount;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== t23 || $[63] !== t25 || $[64] !== t26) {
    t27 = <>{t23}{t25}{t26}</>;
    $[62] = t23;
    $[63] = t25;
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  return t27;
};
export default WaveDropHeader;